import { useMemo } from "react";

import {
  deriveEmailCreativeRefs,
  getSmsValueOptions,
} from "containers/admin/clients/client/campaign-templates-page/sms-channel-rows.utils";
import {
  getStrategyData,
  type StrategyFieldOverrides,
  type StrategyTabFieldOverrides,
} from "containers/admin/clients/client/programs-page/program-strategy-form.utils";

export const CHANNELS_TAB_ID = "channels";

/**
 * Shares one "Value" options list — every email-channel creative as `{ label: <creative name>,
 * value: creativeId }` — across every row of the Channels tab's SMS dynamic-section, through the
 * same per-tab `StrategyFieldOverrides` mechanism `useCampaignObjectiveCascadeOverrides` already
 * uses for the Creatives tabs' `classes`/`contentType`. Email and SMS creatives are disjoint sets,
 * so no per-row self-exclusion is needed here.
 */
export const useSmsChannelValueOptions = (
  draftData: Record<string, unknown>,
  baseOverrides?: StrategyFieldOverrides,
): StrategyFieldOverrides => {
  // Keyed on the creatives sub-tree rather than the whole draftData: `setNestedValue` preserves
  // object identity for untouched branches, so editing an unrelated tab doesn't rebuild this (and,
  // downstream, every tab's schema via `fieldOverrides`/`tabs`).
  const creativesData = getStrategyData(draftData).creatives;
  const valueOptions = useMemo(
    () => getSmsValueOptions(deriveEmailCreativeRefs(draftData)),
    [creativesData],
  );

  return useMemo(() => {
    // `applyFieldOverridesToProperties` matches this key by field name at every depth under the
    // Channels tab, not by path — keep `value` unique to `channels.sms.items[]` within that tab.
    const channelsOverrides: StrategyTabFieldOverrides = {
      ...baseOverrides?.[CHANNELS_TAB_ID],
      value: { "x-options": valueOptions },
    };

    return {
      ...baseOverrides,
      [CHANNELS_TAB_ID]: channelsOverrides,
    };
  }, [baseOverrides, valueOptions]);
};
